// src/components/PaymentSuccess/index.js - TRANSACTION-BASED VERIFICATION
// Only shows success when /web/order/payment-status/{orderSerialNo} confirms
// the order is paid. Public/guest-safe — see the endpoint's own doc comment
// (WebOrderController::paymentStatus) for why this isn't /web/transactions.
//
// ⚠️ IMPORTANT DISTINCTION this file makes (see the root-cause writeup this
// fix addresses): "we haven't confirmed the payment yet" is NOT the same as
// "the payment failed". A genuine failure only ever comes from the gateway
// itself explicitly telling us so (detectImmediateFailure below — Cashfree's
// own failure status, Stripe's non-succeeded redirect, or Razorpay's
// payment.failed handler, which redirects to /payment/cancel and never even
// reaches this component). If the gateway told us the charge went through
// (e.g. Razorpay's payment_id is present) but our OWN /web/transactions
// hasn't recorded it yet — because the backend's webhook pipeline hasn't
// finished — that is genuinely unresolved, not "failed". Declaring it
// failed and pushing the customer back into checkout risks a double charge:
// they already paid once, successfully, and we'd be asking them to pay
// again. This component now has three real outcomes: 'success' (confirmed),
// 'failed' (gateway explicitly said so), and 'pending' (gateway said it
// went through, we just can't confirm our own record yet — kept polling
// quietly in the background, never auto-redirected into a retry).
import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

// Polling config. Two phases:
//  1. "Fast" phase — checked frequently right after redirect, covers the
//     common case where the webhook lands within a few seconds.
//  2. "Background" phase — once the fast phase is exhausted without a
//     confirmed record, we do NOT declare failure. We keep checking, just
//     less frequently, for a much longer window, while showing a calm
//     "still confirming" state instead of a scary failure screen.
const FAST_POLL_ATTEMPTS = 8;
const FAST_POLL_INTERVAL_MS = 3000; // 3 seconds — ~24s of fast polling
const BACKGROUND_POLL_INTERVAL_MS = 10000; // 10 seconds once in the "pending" state
const BACKGROUND_POLL_MAX_ATTEMPTS = 30; // ~5 more minutes of automatic background checking

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('validating'); // 'validating' | 'success' | 'failed' | 'pending'
  const [countdown, setCountdown] = useState(5);
  const [orderInfo, setOrderInfo] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [pollAttempt, setPollAttempt] = useState(0);
  const hasVerified = useRef(false);
  const isMountedRef = useRef(true);
  const backgroundPollTimeoutRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (backgroundPollTimeoutRef.current) clearTimeout(backgroundPollTimeoutRef.current);
    };
  }, []);

  // Helper to safely clear cart locally after a confirmed payment
  const clearLocalCart = () => {
    try {
      const storeDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
      const storeIdToClear = storeDetails ? storeDetails.id : null;

      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && storeIdToClear && key.startsWith(`resto_cart_data_${storeIdToClear}_`)) {
          keysToRemove.push(key);
        } else if (key && !storeIdToClear && key.startsWith('resto_cart_data_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      window.dispatchEvent(new Event('cart_updated'));
      console.log('✅ Cart successfully cleared after confirmed payment.');
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  // ──────────────────────────────────────────────
  // Step 0: Quick-reject from URL params
  // Only for EXPLICIT failure indicators (e.g. Cashfree order_status=ACTIVE)
  // ──────────────────────────────────────────────
  const detectImmediateFailure = () => {
    const gateway = searchParams.get('gateway')?.toLowerCase();

    // ─── CASHFREE: Check order_status / transaction_status from redirect ───
    if (gateway === 'cashfree') {
      const cfOrderStatus = searchParams.get('order_status');
      const cfTxnStatus = searchParams.get('transaction_status');

      if (cfOrderStatus) {
        const s = cfOrderStatus.toUpperCase();
        if (['EXPIRED', 'TERMINATED', 'CANCELLED', 'VOID'].includes(s)) {
          console.log(`🛑 [PaymentSuccess] Cashfree immediate reject: order_status=${s}`);
          return {
            failed: true,
            message: `Payment session ${s.toLowerCase()}. Please try again.`
          };
        }
      }

      if (cfTxnStatus) {
        const t = cfTxnStatus.toUpperCase();
        if (['FAILED', 'USER_DROPPED', 'CANCELLED', 'VOID'].includes(t)) {
          console.log(`🛑 [PaymentSuccess] Cashfree txn reject: transaction_status=${t}`);
          return {
            failed: true,
            message: 'Payment was cancelled or failed at the gateway. No amount was deducted.'
          };
        }
      }

      // NOTE: If NO Cashfree params at all (e.g. backend controls return_url
      // and user navigated back), we do NOT immediately fail here.
      // Instead we fall through to /web/transactions verification below,
      // which is the definitive source of truth.
    }

    // ─── RAZORPAY: No payment_id means payment didn't complete ───
    if (gateway === 'razorpay') {
      const paymentId = searchParams.get('payment_id');
      if (!paymentId) {
        console.log('🛑 [PaymentSuccess] Razorpay: No payment_id in URL.');
        return {
          failed: true,
          message: 'Payment was not completed. Please try again.'
        };
      }
    }

    // ─── STRIPE: redirect_status != succeeded ───
    if (gateway === 'stripe') {
      const stripeStatus = searchParams.get('redirect_status');
      if (stripeStatus && stripeStatus !== 'succeeded') {
        return {
          failed: true,
          message: `Payment ${stripeStatus}. Please try again.`
        };
      }
    }

    return { failed: false };
  };

  // ──────────────────────────────────────────────
  // Step 1: Main verification entry point
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    const orderId = searchParams.get('order');
    const gateway = searchParams.get('gateway')?.toLowerCase();
    const paymentId = searchParams.get('payment_id');
    const cfOrderId = searchParams.get('cf_order_id');

    console.log(`🔍 [PaymentSuccess] Handling redirect: gateway=${gateway}, order=${orderId}, payment_id=${paymentId}, cf_order_id=${cfOrderId}`);
    console.log(`🔍 [PaymentSuccess] Full URL params: ${searchParams.toString()}`);

    if (!orderId) {
      console.warn('⚠️ [PaymentSuccess] Missing order reference.');
      setStatus('failed');
      setVerificationMessage('Transaction identifier missing. Payment could not be verified.');
      return;
    }

    setOrderInfo({ orderId, gateway, paymentId, cfOrderId });

    // Quick reject for explicit failures
    const quickCheck = detectImmediateFailure();
    if (quickCheck.failed) {
      setStatus('failed');
      setVerificationMessage(quickCheck.message);
      setTimeout(() => handleRedirectBackToMenu(true), 4000);
      return;
    }

    // ─── Always verify via /web/transactions (the definitive source) ───
    verifyViaTransactions(orderId, gateway, paymentId, 1);
  }, [searchParams]);

  // ──────────────────────────────────────────────
  // Step 2: Verify payment by checking /web/transactions
  //
  // A transaction record = payment confirmed → 'success'.
  // No transaction record yet after the fast phase = UNCONFIRMED, not
  // failed → 'pending'. We keep checking quietly in the background
  // (slower interval, no visible "attempt" counter) instead of declaring
  // failure and pushing the customer back into checkout — see the header
  // comment for why. Only an explicit gateway failure (handled earlier by
  // detectImmediateFailure, before this function is ever called) sets
  // 'failed'. A genuine, permanent network/API problem also lands in
  // 'pending' (we truly don't know the outcome — that's the opposite of
  // grounds to call it failed), with a manual "Check Again" always available.
  // ──────────────────────────────────────────────
  const verifyViaTransactions = async (orderId, gateway, paymentId, attempt, phase = 'fast') => {
    if (!isMountedRef.current) return;
    try {
      const token = getValidAuthSession()?.token;
      const tenantId = getTenantId();

      console.log(`🌐 [PaymentSuccess] Transaction check (${phase}) attempt ${attempt} for order: ${orderId}`);

      if (phase === 'fast') {
        setVerificationMessage(
          attempt === 1
            ? 'Verifying payment with your bank...'
            : `Confirming payment with ${gateway || 'payment gateway'}... (Attempt ${attempt}/${FAST_POLL_ATTEMPTS})`
        );
        setPollAttempt(attempt);
      }

      const headers = {
        'Authorization': token ? `Bearer ${token}` : undefined,
        'X-Api-Key': X_API_KEY,
        'X-Tenant': tenantId,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      // ─── Check /web/order/payment-status/{orderSerialNo} — public, no
      // login required. Web checkout allows GUEST orders (a customer record
      // is auto-created server-side, but the browser never gets a login
      // session for it), so the customer-scoped, auth-gated
      // /web/transactions (FrontEndCustomerController::myTransactions)
      // always 401'd here for a guest's own payment confirmation — every
      // error is treated as "unconfirmed, keep polling" by design (see file
      // header), so a guest checkout could NEVER resolve to 'success' and
      // was stuck on this screen indefinitely even after the webhook had
      // already marked the order paid. This endpoint only needs the order's
      // own serial number — the same thing already in this page's URL. ───
      const statusResponse = await axios.get(
        `${API_BASE_URL}/web/order/payment-status/${encodeURIComponent(orderId)}`,
        { headers }
      );

      if (!isMountedRef.current) return;
      const statusData = statusResponse.data?.data;

      if (statusData?.is_paid) {
        const transactionNo = statusData.transaction_no || '';
        console.log(`🎉 [PaymentSuccess] CONFIRMED PAID — transaction: ${transactionNo || '(no transaction_no on record)'}`);

        setStatus('success');
        setOrderInfo(prev => ({ ...prev, transactionNo }));
        setVerificationMessage('Payment verified successfully! Your order is being prepared.');
        clearLocalCart();
        return;
      }

      // No transaction found yet
      console.log(`📭 [PaymentSuccess] No transaction record yet for order: ${orderId} (${phase} attempt ${attempt})`);

      if (phase === 'fast' && attempt < FAST_POLL_ATTEMPTS) {
        setStatus('validating');
        setTimeout(() => {
          verifyViaTransactions(orderId, gateway, paymentId, attempt + 1, 'fast');
        }, FAST_POLL_INTERVAL_MS);
        return;
      }

      // ─── Fast phase exhausted (or a background check came up empty
      // again) — still UNCONFIRMED, not failed. Razorpay/Cashfree already
      // told us the charge went through (that's why we're on this page at
      // all — see detectImmediateFailure); our own backend just hasn't
      // recorded it yet. Show a calm "still confirming" state and keep
      // checking quietly in the background — no auto-redirect, no retry
      // prompt, so the customer is never pushed toward paying twice. ───
      setStatus('pending');
      setVerificationMessage(
        "Payment confirmation is taking longer than expected. If your bank has debited the amount, there's no need to pay again — your order will confirm automatically."
      );

      if (attempt < BACKGROUND_POLL_MAX_ATTEMPTS) {
        backgroundPollTimeoutRef.current = setTimeout(() => {
          verifyViaTransactions(orderId, gateway, paymentId, attempt + 1, 'background');
        }, BACKGROUND_POLL_INTERVAL_MS);
      }

    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('❌ [PaymentSuccess] Transaction check error:', error.response?.data || error.message);

      if (phase === 'fast' && attempt < FAST_POLL_ATTEMPTS) {
        setVerificationMessage(`Checking payment status... (Attempt ${attempt})`);
        setTimeout(() => {
          verifyViaTransactions(orderId, gateway, paymentId, attempt + 1, 'fast');
        }, FAST_POLL_INTERVAL_MS);
        return;
      }

      // A request failure tells us nothing about whether the payment
      // succeeded — that's grounds for "unconfirmed", never "failed".
      setStatus('pending');
      setVerificationMessage(
        "We're having trouble reaching our servers to confirm your payment. If your bank has debited the amount, there's no need to pay again — please check again shortly."
      );

      if (attempt < BACKGROUND_POLL_MAX_ATTEMPTS) {
        backgroundPollTimeoutRef.current = setTimeout(() => {
          verifyViaTransactions(orderId, gateway, paymentId, attempt + 1, 'background');
        }, BACKGROUND_POLL_INTERVAL_MS);
      }
    }
  };

  // Manual re-check, offered from the 'pending' screen — restarts a fresh
  // fast-polling burst rather than waiting for the next slow background tick.
  const handleManualRecheck = () => {
    if (backgroundPollTimeoutRef.current) clearTimeout(backgroundPollTimeoutRef.current);
    const orderId = searchParams.get('order');
    const gateway = searchParams.get('gateway')?.toLowerCase();
    const paymentId = searchParams.get('payment_id');
    setStatus('validating');
    setVerificationMessage('Checking payment status...');
    verifyViaTransactions(orderId, gateway, paymentId, 1, 'fast');
  };

  // Step 3: Auto-redirect countdown (only for success)
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const t = setInterval(() => setCountdown(c => c - 1), 1000);
      return () => clearInterval(t);
    }

    if (status === 'success' && countdown === 0) {
      handleRedirectBackToMenu(false);
    }
  }, [status, countdown]);

  const handleRedirectBackToMenu = (isRetry = false) => {
    try {
      const storeDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
      if (storeDetails?.name) {
        const storeSlug = storeDetails.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const targetUrl = `/restaurant/${storeSlug}${isRetry ? '?retryPayment=true' : ''}`;

        console.log(`↪️ [PaymentSuccess] Redirecting user to: ${targetUrl}`);
        window.location.href = targetUrl;
        return;
      }
    } catch (e) {
      console.error('↪️ [PaymentSuccess] Redirect failed - session details missing.');
    }

    window.location.href = '/restaurants';
  };

  // ──────────────────────────────────────────────
  // RENDER: Loading / Validating
  // ──────────────────────────────────────────────
  if (status === 'validating') {
    return (
      <div className="pp-container" key="validating">
        <div className="pp-loader"></div>
        <p className="pp-subtext">
          {verificationMessage || 'Verifying payment with your bank...'}
        </p>
        {pollAttempt > 0 && (
          <div className="pp-poll-indicator">
            <RefreshCw size={14} className="pp-spin-icon" />
            <span>Confirming with payment gateway ({pollAttempt}/{FAST_POLL_ATTEMPTS})</span>
          </div>
        )}
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // RENDER: Failed / Incomplete
  // ──────────────────────────────────────────────
  if (status === 'failed') {
    return (
      <div className="pp-container pp-container--failed" key="failed">
        <div className="pp-failure-circle">
          <AlertCircle size={48} color="#dc2626" />
        </div>
        <h2 className="pp-title pp-title--failed">Payment Not Completed</h2>
        <p className="pp-subtext pp-subtext--failed">{verificationMessage}</p>

        {orderInfo?.orderId && (
          <div className="pp-detail-row">
            <span className="pp-detail-label">Order Ref:</span>
            <span className="pp-detail-value">#{orderInfo.orderId}</span>
          </div>
        )}

        {orderInfo?.gateway && (
          <div className="pp-detail-row">
            <span className="pp-detail-label">Gateway:</span>
            <span className="pp-detail-value pp-capitalize">{orderInfo.gateway}</span>
          </div>
        )}

        <button
          className="pp-manual-redirect pp-manual-redirect--retry"
          onClick={() => handleRedirectBackToMenu(true)}
        >
          Return to Cart & Retry
        </button>

        <p className="pp-help-text">
          If money was deducted, it will be refunded within 1-3 business days.
        </p>
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // RENDER: Pending / Unconfirmed
  // The gateway told us the charge went through; we just can't confirm our
  // own record yet. Deliberately calmer than the 'failed' screen, no retry-
  // payment prompt, and no auto-redirect anywhere in this branch.
  // ──────────────────────────────────────────────
  if (status === 'pending') {
    return (
      <div className="pp-container pp-container--pending" key="pending">
        <div className="pp-pending-circle">
          <Clock size={48} color="#b45309" />
        </div>
        <h2 className="pp-title pp-title--pending">Confirming Your Payment</h2>
        <p className="pp-subtext">{verificationMessage}</p>

        {orderInfo?.orderId && (
          <div className="pp-detail-row">
            <span className="pp-detail-label">Order Ref:</span>
            <span className="pp-detail-value">#{orderInfo.orderId}</span>
          </div>
        )}

        {orderInfo?.gateway && (
          <div className="pp-detail-row">
            <span className="pp-detail-label">Gateway:</span>
            <span className="pp-detail-value pp-capitalize">{orderInfo.gateway}</span>
          </div>
        )}

        <button
          className="pp-manual-redirect pp-manual-redirect--pending"
          onClick={handleManualRecheck}
        >
          <RefreshCw size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
          Check Again
        </button>

        <button
          className="pp-manual-redirect pp-manual-redirect--secondary"
          onClick={() => navigate('/orders')}
        >
          View My Orders
        </button>

        <p className="pp-help-text">
          This page will keep checking automatically. It's safe to close it
          and check your order history later — please don't place a new
          order for the same items until you've confirmed this one didn't
          go through.
        </p>
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // RENDER: Success
  // ──────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="pp-container" key="success">
        <div className="pp-success-circle">
          <CheckCircle size={48} color="#16a34a" />
        </div>
        <h2 className="pp-title">Order Placed Successfully!</h2>

        {orderInfo?.orderId && (
          <div className="pp-order-id">
            <span className="pp-order-id-label">Order ID:</span>
            <span className="pp-order-id-value">#{orderInfo.orderId}</span>
          </div>
        )}

        {orderInfo?.transactionNo && (
          <div className="pp-detail-row pp-detail-row--success">
            <span className="pp-detail-label">Txn Ref:</span>
            <span className="pp-detail-value">{orderInfo.transactionNo}</span>
          </div>
        )}

        <div className="pp-subtext" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', textAlign: 'center' }}>
          <div>{verificationMessage || 'Your payment was confirmed and your order is being prepared.'}</div>
          {(() => {
            try {
              const storeDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
              if (storeDetails?.phone) {
                return (
                  <div style={{ marginTop: '0.5rem', fontSize: '1.1rem', color: '#4b5563' }}>
                    Or call us at <strong>{storeDetails.phone}</strong>
                  </div>
                );
              }
            } catch (e) {}
            return null;
          })()}
        </div>

        <div className="pp-redirect">
          Redirecting to store in {countdown}s
        </div>

        <button
          className="pp-manual-redirect"
          onClick={() => handleRedirectBackToMenu(false)}
        >
          Return to Menu Now
        </button>
      </div>
    );
  }

  return (
    <div className="pp-container">
      <div className="pp-loader"></div>
      <p className="pp-subtext">Redirecting…</p>
    </div>
  );
};

export default PaymentSuccess;